import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../../../api/agent";
import {
  AppFilter,
  BigCommerceProductFilter,
  ProductManagerFilter,
  ShopifyProductFilter,
} from "../../../../models/app/AppModels";
import { AppResponse } from "../../../../models/app/AppResponse";
import {
  DealerLocation,
  DmsSetupState,
  LocationPart,
  LocationWholegood,
  MatchedProduct,
  ProductType,
  NotSyncedResponse,
  PricingType,
  AggregateSyncStatusRequest,
  GetWGSerialRequest,
  SetupType,
  TimeZoneOption,
} from "../../../../models/features/Integration/DmsSetup";
import {
  shopifyProduct,
  LinkInventoryDto,
} from "../../../../models/features/Integration/shopify";
import {
  Product,
  ProductManufacturer,
} from "../../../../models/features/Product/Product";
import { DmsLocations, Setup } from "../../../../models/auth/Auth";
import { SetupProfile } from "../../../../models/features/SetupProfile/SetupProfile";
import { PaymentType } from "../../../../models/features/ShoppingPortal/ShoppingPortalDto";
import {
  DmsPart,
  DmsPartRequestDto,
  DmsWhlegoodRequestDto,
} from "../../../../models/features/ShoppingPortal/DmsPart";
import { number } from "yup";
import { DmsManufacturer } from "../../../../models/features/ShoppingPortal/DmsManufacturer";

const initialState: DmsSetupState = {
  wholegoods: { items: [], matchedItems: [], misMatchedItems: [] },
  parts: [],
  itemTypes: [],
  matchedProducts: [],
  missMatchedProducts: [],
  matchedWholegoods: [],
  matchedParts: [],
  locationIds: [],
  typeToProcess: [],
  dmsWholeGoods: [],
  dmsParts: [],
  dealerManufacturers: [],
  missMatchedParts: [],
  stocking: false,
  withQtyOnly: false,
  mappedLocations: [],
  dmsManufacturers: [],
  setupType: SetupType.Shopify,
  siteCredentials: "",
  timezoneId: "",
};

export const dmsSlice = createSlice({
  name: "dms",
  initialState: initialState,
  reducers: {
    addWhoolegoods: (state, action: PayloadAction<LocationWholegood[]>) => {
      state.wholegoods.items = action.payload;
    },
    addParts: (state, action: PayloadAction<LocationPart[]>) => {
      state.parts = action.payload;
    },
    setStocking: (state, action: PayloadAction<boolean>) => {
      state.withQtyOnly = false;
      state.stocking = action.payload;
    },
    setWithQuantityOnly: (state, action: PayloadAction<boolean>) => {
      state.withQtyOnly = action.payload;
    },
    addLocation: (state, action: PayloadAction<string>) => {
      state.locationIds = [...state.locationIds, action.payload];
    },
    addMappedLocations: (state, action: PayloadAction<DealerLocation[]>) => {
      state.mappedLocations = action.payload;
    },
    removeLocation: (state, action: PayloadAction<string>) => {
      state.locationIds = state.locationIds.filter(
        (item) => item !== action.payload,
      );
    },
    removeDmsData: (state) => {
      state = initialState;
    },
    addProductType: (state, action: PayloadAction<string>) => {
      if (!state.typeToProcess.some((n) => n === action.payload))
        state.typeToProcess.push(action.payload);
    },
    addProductTypeList: (state, action: PayloadAction<string[]>) => {
      state.typeToProcess = action.payload;
    },
    removeProductType: (state, action: PayloadAction<string>) => {
      state.typeToProcess = state.typeToProcess.filter(
        (item) => item !== action.payload,
      );
    },
    addMatchedProducts: (state, action: PayloadAction<MatchedProduct[]>) => {
      state.matchedProducts.push(...action.payload);
    },
    addBulkPartMatchedProducts: (
      state,
      action: PayloadAction<MatchedProduct[]>,
    ) => {
      state.matchedProducts.push(...action.payload);
    },
    addBulkMatchedWholegoods: (
      state,
      action: PayloadAction<MatchedProduct[]>,
    ) => {
      state.matchedWholegoods = action.payload;
    },
    addBulkMatchedParts: (state, action: PayloadAction<MatchedProduct[]>) => {
      state.matchedParts = action.payload;
    },
    addMissMatchedProducts: (
      state,
      action: PayloadAction<LocationWholegood[]>,
    ) => {
      state.missMatchedProducts = action.payload;
    },
    addMissMatchedParts: (state, action: PayloadAction<LocationPart[]>) => {
      state.missMatchedParts = action.payload;
    },
    addMatchedProduct: (state, action: PayloadAction<MatchedProduct>) => {
      state.matchedWholegoods.push(action.payload);
    },
    removeMissMatchProduct: (
      state,
      action: PayloadAction<LocationWholegood>,
    ) => {
      state.missMatchedProducts = state.missMatchedProducts.filter(
        (n) => n.model !== action.payload.model,
      );
    },
    removeMissMatchPart: (state, action: PayloadAction<LocationPart>) => {
      state.missMatchedParts = state.missMatchedParts.filter(
        (n) => n.partNumber !== action.payload.partNumber,
      );
    },
    removeBulkMissMatchPart: (state, action: PayloadAction<LocationPart[]>) => {
      state.missMatchedParts = state.missMatchedParts.filter(
        (n) => !action.payload.some((m) => m.partNumber === n.partNumber),
      );
    },
    removeAllMissMatchPart: (state) => {
      state.missMatchedParts = [];
    },
    removeMatchedProduct: (state, action: PayloadAction<MatchedProduct>) => {
      state.matchedWholegoods = state.matchedWholegoods.filter(
        (n) =>
          n.dmsProduct &&
          n.dmsProduct.inventoryModelId !==
            action.payload.dmsProduct?.inventoryModelId,
      );
      // if (action.payload.dmsProduct)
      //   state.missMatchedProducts.push(action.payload.dmsProduct);
    },
    removeMatchPart: (
      state,
      action: PayloadAction<{ inventoryPartIds: string[] }>,
    ) => {
      state.matchedParts = state.matchedParts.filter(
        (n) =>
          n.dmsPart &&
          !action.payload.inventoryPartIds.includes(n.dmsPart.inventoryPartId),
      );
    },
    removeMatchedProducts: (
      state,
      action: PayloadAction<{ inventoryModelIds: string[] }>,
    ) => {
      state.matchedWholegoods = state.matchedWholegoods.filter(
        (n) =>
          n.dmsProduct &&
          !action.payload.inventoryModelIds.includes(
            n.dmsProduct.inventoryModelId,
          ),
      );
    },
    addMissMatchedProduct: (
      state,
      action: PayloadAction<LocationWholegood>,
    ) => {
      state.missMatchedProducts.push(action.payload);
    },
    addStoreSettings: (
      state,
      action: PayloadAction<{ url: string; token: string; siteName: string }>,
    ) => {
      state.storeUrl = action.payload.url;
      state.storeAccessToken = action.payload.token;
      state.siteName = action.payload.siteName;
    },
    addDmsWhoolegoods: (
      state,
      action: PayloadAction<{ whoolegoods: LocationWholegood[] }>,
    ) => {
      state.dmsWholeGoods = action.payload.whoolegoods;
    },
    addDealerManufacturers: (
      state,
      action: PayloadAction<{ manufacturer: ProductManufacturer[] }>,
    ) => {
      state.dealerManufacturers = action.payload.manufacturer;
    },
    addDmsParts: (state, action: PayloadAction<{ parts: LocationPart[] }>) => {
      state.dmsParts = action.payload.parts;
    },
    setSelectManufacturer: (
      state,
      action: PayloadAction<{ dmsManufacturers: DmsManufacturer[] }>,
    ) => {
      console.log("dmsManufacturers", action.payload);
      state.dmsManufacturers = action.payload.dmsManufacturers;
    },
    removeDmsWhoolegoods: (
      state,
      action: PayloadAction<{ inventoryModelId: string }>,
    ) => {
      state.dmsWholeGoods = state.dmsWholeGoods.filter(
        (n) => n.inventoryModelId !== action.payload.inventoryModelId,
      );
    },
    removeDmsParts: (
      state,
      action: PayloadAction<{ inventoryPartId: string }>,
    ) => {
      state.dmsParts = state.dmsParts?.filter(
        (n) => n.inventoryPartId !== action.payload.inventoryPartId,
      );
    },
    removeAllDmsWhoolegoods: (state) => {
      state.dmsWholeGoods = [];
    },
    removeAllDmsParts: (state) => {
      state.dmsParts = [];
    },
    setIsSyncCompleted: (state, action: PayloadAction<{ status: boolean }>) => {
      state.IsSyncCompleted = action.payload.status;
    },
    removeDmsManufacturer: (state, action: PayloadAction<{ id: string }>) => {
      state.dmsManufacturers = state.dmsManufacturers.filter(
        (m) => m.id !== action.payload.id,
      );
    },
    addShopifyUrl: (state, action: PayloadAction<{ url: string }>) => {
      state.storeUrl = action.payload.url;
    },
    addShopifyAccessToken: (
      state,
      action: PayloadAction<{ token: string }>,
    ) => {
      state.storeAccessToken = action.payload.token;
    },
    addSiteCredentials: (
      state,
      action: PayloadAction<{ credentials: string }>,
    ) => {
      state.siteCredentials = action.payload.credentials;
    },
    addSetupType: (state, action: PayloadAction<{ setupType: SetupType }>) => {
      state.setupType = action.payload.setupType;
    },
    setTimezone: (state, action: PayloadAction<{ timezoneId: string }>) => {
      state.timezoneId = action.payload.timezoneId;
    },
  },
});

export const dmsCatalogApi = createApi({
  reducerPath: "dmsCatalogApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    addProductToShopify: build.mutation<
      AppResponse<shopifyProduct>,
      {
        data: {
          catalogProductId: string;
          catalogProduct?: Product;
          dmsProduct: LocationWholegood;
        };
      }
    >({
      query({ data }) {
        return {
          url: `api/Shopify/AddProduct`,
          method: "post",
          data: data,
        };
      },
    }),
    saveSetup: build.mutation<
      AppResponse<Setup>,
      {
        data: {
          dmsLocations: DealerLocation[];
          matchingProducts: MatchedProduct[];
          storeAccessToken: string;
          storeUrl: string;
          siteName: string;
          dmsDealerId: string;
          dealerId: string;
          everestDmsDealerId: string;
          productTypes: string[];
          dmsManufacturers?: DmsManufacturer[];
          setupType: SetupType;
          siteCredentials: string;
          DealerTimeZone?: string;
        };
      }
    >({
      query({ data }) {
        return {
          url: `api/SaveSetup`,
          method: "post",
          data: data,
        };
      },
    }),
    reInitiateSetup: build.mutation<
      AppResponse<Setup>,
      {
        data: {
          matchingProducts: MatchedProduct[];
          dealerId: string;
          productTypes: string[];
        };
      }
    >({
      query({ data }) {
        return {
          url: `api/ReInitiateSetup`,
          method: "post",
          data: data,
        };
      },
    }),
    getProductAggregates: build.mutation<
      any,
      { setupId: string; paging: AppFilter; productType: number }
    >({
      query: ({ setupId, paging, productType }) => ({
        url: `api/Shopify/GetProductAggregate?productType=${productType}`,
        method: "post",
        data: paging,
      }),
    }),
    getAggregateProducts: build.mutation<
      any,
      { paging: AppFilter; productType: number }
    >({
      query: ({ paging, productType }) => ({
        url: `api/Shopify/GetAggregateProducts?productType=${productType}`,
        method: "post",
        data: paging,
      }),
    }),

    getSyncedProducts: build.mutation<
      any,
      { setupId: string; paging: ProductManagerFilter; productType: number }
    >({
      query: ({ setupId, paging, productType }) => ({
        url: `api/Shopify/GetSyncedProducts?productType=${productType}`,
        method: "post",
        data: paging,
      }),
    }),
    addProductToShopifyById: build.mutation<
      any,
      { productAggregateId: string }
    >({
      query: (data) => ({
        url: `api/Shopify/AddProductToShopifyById`,
        method: "post",
        data: data,
      }),
    }),
    syncShopifyToAggregate: build.mutation<
      any,
      {
        aggregateId: string;
        shopifyProductId: number;
        handle: string;
      }
    >({
      query: (data) => ({
        url: `api/Shopify/SyncShopToAggregate`,
        method: "post",
        data: data,
      }),
    }),
    syncShopifyToAggregateWithDMS: build.mutation<
      any,
      {
        Wholegood: any;
        Part: any;
        Handle: string;
        productType: ProductType;
        shopifyProductId: number;
        shopifyProductVariantId: number;
        isUsedProduct: boolean;
      }
    >({
      query: (data) => ({
        url: `api/Shopify/syncShopifyToAggregateWithDMS`,
        method: "post",
        data: data,
      }),
    }),
    setProductStatus: build.mutation<
      any,
      { products: any[]; decision: string; setupType: SetupType }
    >({
      query: (data) => ({
        url: `api/Store/SetProductStatus`,
        method: "post",
        data: data,
      }),
    }),
    InventoryStockDecision: build.mutation<
      any,
      { products: any[]; decision: string }
    >({
      query: (data) => ({
        url: `api/Shopify/InventoryStockDecision`,
        method: "post",
        data: data,
      }),
    }),
    removeDealerSetup: build.mutation<any, { setupId: string }>({
      query: (data) => ({
        url: `api/Store/RemoveDealerSetup`,
        method: "delete",
        data: data,
      }),
    }),
    verifyAccessToken: build.mutation<
      any,
      { shopUrl: string; shopAccessToken: string; dmsdealerId?: string }
    >({
      query: (data) => ({
        url: `api/Shopify/VerifyAccessToken`,
        method: "post",
        data: data,
      }),
    }),
    getStoreLocations: build.mutation<
      any,
      { storeUrl: string; accessToken?: string; setupType: SetupType }
    >({
      query: (data) => ({
        url: `api/Store/GetStoreLocations`,
        method: "post",
        data: data,
      }),
    }),
    getDmsSetupLocations: build.mutation<any, { dmsSetupId: string }>({
      query: (data) => ({
        url: `api/Shopify/GetDmsSetupLocations`,
        method: "post",
        data: data,
      }),
    }),
    GetMatchingProducts: build.mutation<
      AppResponse<MatchedProduct>,
      {
        wholegoods: LocationWholegood[];
        parts: LocationPart[];
        dmsSetupId?: string;
        dmsManufacturers?: DmsManufacturer[];
      }
    >({
      query: (data) => ({
        url: `api/DmsSetup/GetMatchingWhoolegoods`,
        method: "post",
        data: data,
      }),
    }),
    GetMatchingProductsV2: build.mutation<
      AppResponse<MatchedProduct>,
      {
        wholegoods: LocationWholegood[];
        parts: LocationPart[];
        dmsSetupId?: string;
        dmsManufacturers?: DmsManufacturer[];
      }
    >({
      query: (data) => ({
        url: `api/v2/DmsSetup/GetMatchingWhoolegoods`,
        method: "post",
        data: data,
      }),
    }),
    getDealerManufacturers: build.query<AppResponse<DmsManufacturer>, {}>({
      query: () => ({
        url: `api/Shopify/GetDealerManufacturers`,
      }),
    }),
    getExistingDmsData: build.query<AppResponse<any>, {}>({
      query: () => ({
        url: `api/DmsSetup/GetExistingDmsData`,
      }),
    }),
    setIsSyncing: build.mutation<
      any,
      {
        setupId: string;
        setSyncing: boolean;
        pricing: PricingType;
      }
    >({
      query: (data) => ({
        url: `api/DmsSetup/SetSyncing`,
        method: "post",
        data: data,
      }),
    }),
    setIsSyncingV2: build.mutation<
      any,
      {
        dmsSetupId: string;
        setSyncing: boolean;
        pricing: PricingType;
        productType: ProductType;
      }
    >({
      query: (data) => ({
        url: `api/DmsSetup/V2/SetSyncing`,
        method: "post",
        data: data,
      }),
    }),
    GetNotSyncedProducts: build.mutation<
      AppResponse<NotSyncedResponse>,
      { wholegoods: LocationWholegood[]; parts: LocationPart[] }
    >({
      query: (data) => ({
        url: `api/DmsSetup/GetNotSyncedProducts`,
        method: "post",
        data: data,
      }),
    }),
    SetPaymentType: build.mutation<AppResponse<any>, { type: PaymentType }>({
      query: (data) => ({
        url: `api/DmsSetup/SetPaymentType?type=${data.type}`,
        method: "post",
        data: null,
      }),
    }),
    getDmsSetupById: build.mutation<AppResponse<SetupProfile>, {}>({
      query: () => ({
        url: `api/DmsSetup/GetDmsSetupById`,
        method: "get",
      }),
    }),
    getDealerPaymentMethods: build.mutation<
      AppResponse<any>,
      { dealerId: string }
    >({
      query: ({ dealerId }) => ({
        url: `api/DmsSetup/GetDealerPaymentMethods`,
        method: "post",
        data: { methodRequest: { dealerId: dealerId } },
      }),
    }),
    getCustomerCategories: build.mutation<
      AppResponse<any>,
      { dealerId: string }
    >({
      query: ({ dealerId }) => ({
        url: `api/DmsSetup/getCustomerCategories`,
        method: "post",
        data: { methodRequest: { dealerId: dealerId } },
      }),
    }),
    updateSetupProfile: build.mutation<
      AppResponse<any>,
      { data: SetupProfile }
    >({
      query: ({ data }) => ({
        url: `api/DmsSetup/UpdateDmsSetup`,
        method: "post",
        data: { setupRequest: data },
      }),
    }),
    updateSetupLocation: build.mutation<
      void,
      {
        locationId: string;
        storeLocationId: number;
        dealerId: string;
        dmsdealerId: string;
      }
    >({
      query: ({
        locationId,
        storeLocationId: storeLocationId,
        dealerId,
        dmsdealerId,
      }) => ({
        url: `api/DmsSetup/UpdateDmsLocation`,
        method: "put",
        data: {
          locationId: locationId,
          storeLocationId: storeLocationId,
          dealerId: dealerId,
          dmsdealerId: dmsdealerId,
        },
      }),
    }),
    AddSetupLocation: build.mutation<
      AppResponse<DealerLocation>,
      { location: DealerLocation }
    >({
      query: ({ location }) => ({
        url: `api/DmsSetup/AddDmsLocation`,
        method: "post",
        data: { request: location },
      }),
    }),
    UpdateActualSetupLocation: build.mutation<
      AppResponse<DealerLocation>,
      { location: DealerLocation }
    >({
      query: ({ location }) => ({
        url: `api/DmsSetup/UpdateActualDmsLocation`,
        method: "post",
        data: { request: location },
      }),
    }),
    getDmsWholegoods: build.mutation<any, { data: DmsWhlegoodRequestDto }>({
      query: ({ data }) => ({
        url: `api/DmsSetup/GetDmsWholegoods`,
        method: "post",
        data: data,
      }),
    }),
    getDmsWholegoodsV2: build.mutation<any, { data: DmsWhlegoodRequestDto }>({
      query: ({ data }) => ({
        url: `api/DmsSetup/V2/GetDmsWholegoods`,
        method: "post",
        data: data,
      }),
    }),
    getNotSyncedDmsWholegoods: build.mutation<
      any,
      { data: DmsWhlegoodRequestDto }
    >({
      query: ({ data }) => ({
        url: `api/DmsSetup/GetNotSyncedWholegoods`,
        method: "post",
        data: data,
      }),
    }),
    setDmsSyncStatus: build.mutation<
      any,
      {
        request: AggregateSyncStatusRequest;
      }
    >({
      query: ({ request }) => ({
        url: `api/DmsSetup/SetAggregateSyncStatus`,
        method: "post",
        data: request,
      }),
    }),
    setPriceSyncingType: build.mutation<
      any,
      {
        pricing: PricingType;
        productType: ProductType;
        syncAll: boolean;
        productIds: string[];
      }
    >({
      query: (data) => ({
        url: `api/DmsSetup/SetPriceSyncingType`,
        method: "post",
        data: data,
      }),
    }),
    getDealerLocations: build.mutation<
      AppResponse<DealerLocation>,
      { everestDmsDealerId: string }
    >({
      query: (data) => ({
        url: `api/DmsSetup/GetDealerLocations?everestDmsDealerId=${data.everestDmsDealerId}`,
        method: "post",
        data: null,
      }),
    }),
    updateAggregatePrice: build.mutation<
      any,
      {
        aggregateId: string;
        externalProductId: string;
        variantId: number;
        hasActivePromotion: boolean;
        price: number;
        aggregatePriceType: PricingType;
        compareAtPrice: number;
        activePromotion?: any;
      }
    >({
      query: (request) => ({
        url: `api/Shopify/UpdateAggregatePrice`,
        method: "post",
        data: request,
      }),
    }),
    getCatalogSyncedProducts: build.mutation<
      AppResponse<DealerLocation>,
      { productId: string }
    >({
      query: (data) => ({
        url: `api/Product/GetSyncedProducts?productId=${data.productId}`,
        method: "post",
        data: null,
      }),
    }),
    getAggProductsByShopId: build.mutation<
      any,
      { shopifyProductId: string; variantId: string }
    >({
      query: (data) => ({
        url: `api/Shopify/GetAggProductsByShopifyId`,
        method: "post",
        data: data,
      }),
    }),
    getDMSManufacturersBySettingsId: build.query<
      AppResponse<DmsManufacturer>,
      { dealerSettingsId: string }
    >({
      query: ({ dealerSettingsId }) => ({
        url: `api/Manufacturer/GetDmsManufacturersBySettingsId?dealerSettingsId=${dealerSettingsId}`,
      }),
    }),
    addDmsManufacturer: build.mutation<
      any,
      {
        request: any;
      }
    >({
      query: ({ request }) => ({
        url: `api/Manufacturer/AddDmsManufacturer`,
        method: "post",
        data: request,
      }),
    }),
    QuickSync: build.mutation<any, { productId: string }>({
      query: (request) => ({
        url: `api/DmsSetup/QuickSync`,
        method: "post",
        data: request,
      }),
    }),
    deleteDmsManufacturer: build.mutation<any, { mfrId: string }>({
      query: (data) => ({
        url: `api/Manufacturer/DeleteDmsManufacturer?mfrId=${data.mfrId}`,
        method: "delete",
      }),
    }),
    updateDmsManufacturerDisplayName: build.mutation<
      any,
      { manufacturer: DmsManufacturer }
    >({
      query: ({ manufacturer }) => ({
        url: `api/Manufacturer/UpdateDmsManufacturerDisplayName`,
        method: "post",
        data: manufacturer,
      }),
    }),
    hidePricesInBulk: build.mutation<
      any,
      { externalProductIds: number[]; hidePrice: boolean }
    >({
      query: (request) => ({
        url: `api/Store/HidePricesInBulk`,
        method: "post",
        data: request,
      }),
    }),
    SetDMSLocationDefault: build.mutation<
      any,
      { locationId: string; isDefault: boolean }
    >({
      query: (request) => ({
        url: `api/DmsSetup/SetDMSLocationDefault`,
        method: "post",
        data: request,
      }),
    }),
    getNotSyncedDmsParts: build.mutation<
      AppResponse<DmsPart>,
      { data: DmsPartRequestDto }
    >({
      query: ({ data }) => ({
        url: `api/DmsSetup/GetNonSyncedDmsParts`,
        method: "post",
        data: data,
      }),
    }),
    getMiscInventoryData: build.mutation<
      AppResponse<any>,
      { search: string; dealerId: string; PageNumber: number; PageSize: number }
    >({
      query: (data) => ({
        url: `api/DmsSetup/GetMiscInventoryData`,
        method: "post",
        data: data,
      }),
    }),
    getAvailablePriceTypes: build.query<
      AppResponse<any>,
      { producType: ProductType }
    >({
      query: ({ producType }) => ({
        url: `api/Shopify/GetAvailablePriceTypes?productType=${producType}`,
      }),
    }),
    resetPriceSyncType: build.mutation<
      any,
      {
        pricing: PricingType;
        productType: ProductType;
        productIds: string[];
      }
    >({
      query: (data) => ({
        url: `api/DmsSetup/ResetPriceSyncType`,
        method: "post",
        data: data,
      }),
    }),
    fetchBulkOperationStatus: build.mutation<any, null>({
      query: () => ({
        url: `api/DmsSetup/FetchBulkOperationStatus`,
        method: "post",
      }),
    }),
    getDmsSetupsByDmsDealerId: build.mutation<
      AppResponse<Setup[]>,
      { dmsDealerId: string }
    >({
      query: ({ dmsDealerId }) => ({
        url: `api/DmsSetup/GetDmsSetupsByDmsDealerId?dmsDealerId=${dmsDealerId}`,
      }),
    }),
    getNotSyncedShopifyProducts: build.mutation<
      any,
      { paging: ShopifyProductFilter }
    >({
      query: ({ paging }) => ({
        url: `api/Shopify/GetNotSyncedShopifyProducts`,
        method: "post",
        data: paging,
      }),
    }),
    matchShopifyProductsWithDMS: build.mutation<
      any,
      { request: LinkInventoryDto[] }
    >({
      query: ({ request }) => ({
        url: `api/Shopify/MatchShopifyProductsWithDMS`,
        method: "post",
        data: request,
      }),
    }),
    saveMatchedLinkInventory: build.mutation<
      any,
      { request: LinkInventoryDto[] }
    >({
      query: ({ request }) => ({
        url: `api/Shopify/SaveMatchedLinkInventory`,
        method: "post",
        data: request,
      }),
    }),
    getDealerSetupByDmsSetupId: build.mutation<
      AppResponse<Setup>,
      { dmsSetupId: string | number }
    >({
      query: ({ dmsSetupId }) => ({
        url: `api/DmsSetup/GetDealerSetupByDmsSetupId?dmsSetupId=${dmsSetupId}`,
      }),
    }),
    getShopifyInstall: build.mutation<
      AppResponse<any>,
      {
        shopUrl: string;
        clientId: string;
        clientSecret: string;
        dmsDealerId: string;
        everestDmsDealerId: string;
      }
    >({
      query: (data) => ({
        url: `api/Shopify/Install`,
        method: "post",
        data: data,
      }),
    }),
    ValidateBCCredentials: build.mutation<
      any,
      { StoreUrl: string; AccessToken: string; dmsDealerId: string }
    >({
      query: (data) => ({
        url: `api/Store/ValidateBCCredentials`,
        method: "post",
        data: data,
      }),
    }),
    getNotSyncedBigCommerceProducts: build.mutation<
      any,
      { paging: BigCommerceProductFilter }
    >({
      query: ({ paging }) => ({
        url: `api/Store/GetNotSyncedBigCommerceProducts`,
        method: "post",
        data: paging,
      }),
    }),
    
    getTimezones: build.query<AppResponse<TimeZoneOption[]>, void>({
      query: () => ({
        url: `api/DmsSetup/GetTimeZones`,
      }),
    }),
  }),
});

export const {
  useAddProductToShopifyMutation,
  useSaveSetupMutation,
  useReInitiateSetupMutation,
  useGetProductAggregatesMutation,
  useAddProductToShopifyByIdMutation,
  useRemoveDealerSetupMutation,
  useSetProductStatusMutation,
  useInventoryStockDecisionMutation,
  useVerifyAccessTokenMutation,
  useGetMatchingProductsMutation,
  useGetSyncedProductsMutation,
  useGetDealerManufacturersQuery,
  useGetStoreLocationsMutation,
  useGetDmsSetupLocationsMutation,
  useGetExistingDmsDataQuery,
  useSetIsSyncingMutation,
  useSyncShopifyToAggregateMutation,
  useGetNotSyncedProductsMutation,
  useGetAggregateProductsMutation,
  useSyncShopifyToAggregateWithDMSMutation,
  useSetPaymentTypeMutation,
  useGetDmsSetupByIdMutation,
  useGetDealerPaymentMethodsMutation,
  useGetCustomerCategoriesMutation,
  useUpdateSetupProfileMutation,
  useUpdateSetupLocationMutation,
  useUpdateActualSetupLocationMutation,
  useAddSetupLocationMutation,
  useGetDmsWholegoodsMutation,
  useSetDmsSyncStatusMutation,
  useSetPriceSyncingTypeMutation,
  useGetDealerLocationsMutation,
  useUpdateAggregatePriceMutation,
  useGetCatalogSyncedProductsMutation,
  useGetAggProductsByShopIdMutation,
  useGetNotSyncedDmsWholegoodsMutation,
  useGetDMSManufacturersBySettingsIdQuery,
  useGetMatchingProductsV2Mutation,
  useAddDmsManufacturerMutation,
  useDeleteDmsManufacturerMutation,
  useUpdateDmsManufacturerDisplayNameMutation,
  useGetDmsWholegoodsV2Mutation,
  useSetIsSyncingV2Mutation,
  useQuickSyncMutation,
  useHidePricesInBulkMutation,
  useSetDMSLocationDefaultMutation,
  useGetNotSyncedDmsPartsMutation,
  useGetMiscInventoryDataMutation,
  useGetAvailablePriceTypesQuery,
  useResetPriceSyncTypeMutation,
  useFetchBulkOperationStatusMutation,
  useGetDmsSetupsByDmsDealerIdMutation,
  useGetNotSyncedShopifyProductsMutation,
  useMatchShopifyProductsWithDMSMutation,
  useSaveMatchedLinkInventoryMutation,
    useGetDealerSetupByDmsSetupIdMutation,
  useGetShopifyInstallMutation,
    useValidateBCCredentialsMutation,
  useGetTimezonesQuery,
  useGetNotSyncedBigCommerceProductsMutation,
} = dmsCatalogApi;

export const {
  addWhoolegoods,
  addParts,
  addLocation,
  addMappedLocations,
  addMatchedProducts,
  addMissMatchedProducts,
  addMatchedProduct,
  removeMissMatchProduct,
  addMissMatchedProduct,
  removeMatchedProduct,
  removeMatchPart,
  removeMatchedProducts,
  removeLocation,
  addProductType,
  removeProductType,
  removeDmsData,
  addStoreSettings,
  addDmsWhoolegoods,
  addDealerManufacturers,
  addDmsParts,
  addMissMatchedParts,
  removeMissMatchPart,
  addBulkPartMatchedProducts,
  addBulkMatchedWholegoods,
  addBulkMatchedParts,
  removeBulkMissMatchPart,
  removeAllMissMatchPart,
  setStocking,
  setWithQuantityOnly,
  addProductTypeList,
  setSelectManufacturer,
  removeDmsWhoolegoods,
  removeDmsParts,
  removeAllDmsWhoolegoods,
  removeAllDmsParts,
  setIsSyncCompleted,
  removeDmsManufacturer,
  addShopifyUrl,
  addShopifyAccessToken,
  addSiteCredentials,
  addSetupType,
  setTimezone,
} = dmsSlice.actions;
export const dmsReducer = dmsSlice.reducer;
